import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useData } from "../../hooks/useData";
import Icon from "../Icon";
import GlowButton from "../ui-custom/GlowButton";
import { Reveal } from "../ui-custom/Reveal";

/*
 * Contact form. No database. Delivery is provider-agnostic: set endpoint URLs
 * in /data/contact.json -> "endpoints" (email / sms / whatsapp / telegram) and
 * they will be POSTed to on submit. Until then, submission shows a success toast.
 */
async function dispatch(endpoints, payload) {
  const jobs = [];
  ["email", "sms", "whatsapp", "telegram"].forEach((ch) => {
    const url = endpoints?.[ch];
    if (url) {
      jobs.push(
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ channel: ch, ...payload }),
        }).catch(() => null)
      );
    }
  });
  if (jobs.length) await Promise.allSettled(jobs);
  return jobs.length > 0;
}

export default function ContactForm() {
  const { data } = useData("contact");
  const [form, setForm] = useState({});
  const [sending, setSending] = useState(false);
  if (!data) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const sent = await dispatch(data.endpoints, form);
    setSending(false);
    toast.success(sent ? "Message sent! We'll be in touch soon." : "Thanks! We've received your request.", {
      description: sent ? "Your request has been received." : "Connect an endpoint in contact.json to auto-deliver messages.",
    });
    setForm({});
    e.target.reset();
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
      <Reveal className="space-y-4">
        {data.channels.map((c) => (
          <div key={c.type} className="flex items-center gap-4 rounded-2xl glass p-5" data-testid={`contact-channel-${c.type}`}>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft text-brand">
              <Icon name={c.icon} size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">{c.label}</p>
              <p className="text-sm font-medium text-slate-900">{c.value}</p>
            </div>
          </div>
        ))}
      </Reveal>

      <Reveal delay={0.1}>
        <form onSubmit={submit} className="rounded-3xl glass-strong p-7" data-testid="contact-form">
          <div className="grid gap-4 sm:grid-cols-2">
            {data.form.fields.map((f) => {
              const common = {
                required: f.required,
                value: form[f.name] || "",
                onChange: (e) => setForm((p) => ({ ...p, [f.name]: e.target.value })),
                className: "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-[color:var(--brand)] focus:ring-2 focus:ring-[color:var(--brand)]/20",
                "data-testid": `contact-field-${f.name}`,
                placeholder: f.label,
              };
              const wrap = f.type === "textarea" || f.type === "select" ? "sm:col-span-2" : "";
              return (
                <div key={f.name} className={wrap}>
                  <label className="mb-1.5 block text-xs text-slate-500">{f.label}{f.required && " *"}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={4} {...common} />
                  ) : f.type === "select" ? (
                    <select {...common}>
                      <option value="">Select…</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} {...common} />
                  )}
                </div>
              );
            })}
          </div>
          <GlowButton className="mt-6 w-full" icon={<Send size={16} />} data-testid="contact-submit" disabled={sending}>
            {sending ? "Sending…" : data.form.submitLabel}
          </GlowButton>
        </form>
      </Reveal>
    </div>
  );
}
